import { useGlobalSearchStore } from '../../stores';
import { storeToRefs } from 'pinia';
import { SearchFilterBucket } from '../../../shared/models/responses/search/findGeneric.response';
import { docTypes } from '../../../shared/enums/docTypes';
import i18n from '../../../shared/locales/i18n';
import { portalRoles } from '../../../shared/enums/portalRoles';
import { computed } from 'vue';
import { groupTypes as GroupTypeEnum } from '../../../shared/enums/groupTypes';

interface ProfileOption {
  value: portalRoles;
  label: string;
  selectedLabel: string;
}

interface GroupOption {
  value: GroupTypeEnum;
  label: string;
  selectedLabel: string;
}

interface SortOption {
  value: 'asc' | 'desc';
  label: string;
}

export const useSearchFilter = () => {
  const t = i18n.t.bind(i18n);
  const globalSearchStore = useGlobalSearchStore();

  const {
    docType: activeFilter,
    filters,
    profileFilters,
    groupFilters,
    roles,
    groupTypes,
    mediaSortOrder,
  } = storeToRefs(globalSearchStore);

  const role = computed({
    get() {
      if (roles.value?.length > 0) {
        return roles.value[0];
      }
      return null;
    },
    set(value: portalRoles) {
      roles.value = value ? [value] : [];
    },
  });

  const groupType = computed({
    get() {
      if (groupTypes.value?.length > 0) {
        return groupTypes.value[0];
      }
      return null;
    },
    set(value: GroupTypeEnum) {
      groupTypes.value = value ? [value] : [];
    },
  });

  const selectedRoleLabel = computed(
    () => profileOptions.value.find(option => option.value === role.value)?.selectedLabel || ''
  );

  const selectedGroupTypeLabel = computed(
    () => groupOptions.value.find(option => option.value === groupType.value)?.selectedLabel || ''
  );

  const mediaSort = computed<'asc' | 'desc'>({
    get() {
      return mediaSortOrder.value;
    },
    set(value: 'asc' | 'desc') {
      mediaSortOrder.value = value;
    },
  });

  const sortOptions = computed<SortOption[]>(() => [
    { value: 'desc', label: t('label.sort_newest_first') },
    { value: 'asc', label: t('label.sort_oldest_first') },
  ]);

  const canFilterProfile = computed(() => activeFilter.value === docTypes.PROFILE);

  const canFilterGroupType = computed(() => activeFilter.value === docTypes.GROUP);

  const canSortMedia = computed(() => activeFilter.value === docTypes.MEDIA);

  const activeFilterCount = computed(() => filters.value.find(filter => filter.key === activeFilter.value)?.count || 0);

  const setActiveFilter = (value: docTypes) => {
    globalSearchStore.setDocType(value);
  };

  const getFilterLabel = (filter: SearchFilterBucket) => {
    switch (filter.key) {
      case docTypes.PROFILE:
        return t('label.persons');
      case docTypes.GROUP:
        return t('label.groups');
      case docTypes.INTERNAL_SECURE_FILE:
        return t('label.secure_files');
      case docTypes.EXTERNAL_SECURE_FILE:
        return t('label.secure_files_imported');
      case docTypes.IMPORTANT_FILE:
        return t('label.common_files');
      case docTypes.THREAD_MESSAGE:
        return t('label.messages');
      case docTypes.POST:
        return t('label.posts');
      case docTypes.EVENT:
        return t('label.calendar');
      case docTypes.MEDIA:
        return t('label.gallery');
      default:
        return '';
    }
  };

  const getProfileLabel = (role: portalRoles) => {
    switch (role) {
      case portalRoles.EMPLOYEE:
        return t('user.employees');
      case portalRoles.GUARDIAN:
        return t('user.guardians');
      case portalRoles.CHILD:
        return t('user.children');
      default:
        return '';
    }
  };

  const getGroupLabel = (type: GroupTypeEnum) => {
    switch (type) {
      case GroupTypeEnum.INSTITUTIONAL:
        return t('label.aula_groups');
      default:
        return t('label.cross_groups');
    }
  };

  const profileOptions = computed<ProfileOption[]>(() => {
    const options: ProfileOption[] = profileFilters.value.map(filter => ({
      value: filter.key as portalRoles,
      label: `${getProfileLabel(filter.key as portalRoles)} (${filter.count})`,
      selectedLabel: `${t('label.persons')} (${getProfileLabel(filter.key as portalRoles)})`,
    }));
    return [
      {
        value: null,
        label: `${t('label.all')} (${activeFilterCount.value})`,
        selectedLabel: `${t('label.persons')} (${t('label.all')})`,
      } as ProfileOption,
      ...options,
    ];
  });

  const groupOptions = computed<GroupOption[]>(() => {
    const options: GroupOption[] = groupFilters.value.map(filter => ({
      value: filter.key as GroupTypeEnum,
      label: `${getGroupLabel(filter.key as GroupTypeEnum)} (${filter.count})`,
      selectedLabel: `${t('label.groups')} (${getGroupLabel(filter.key as GroupTypeEnum)})`,
    }));
    return [
      {
        value: null,
        label: `${t('label.all')} (${activeFilterCount.value})`,
        selectedLabel: `${t('label.groups')} (${t('label.all')})`,
      } as GroupOption,
      ...options,
    ];
  });

  return {
    activeFilter,
    filters,
    role,
    groupType,
    mediaSort,
    profileOptions,
    groupOptions,
    sortOptions,
    selectedRoleLabel,
    selectedGroupTypeLabel,
    canFilterProfile,
    canFilterGroupType,
    canSortMedia,
    setActiveFilter,
    getFilterLabel,
  };
};
